<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Voicemail</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="editOperation">Save</el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
          >
            Cancel
          </el-button>
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          />
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits service="Voicemail" :record="selectedVoicemail" />
      <el-tabs v-model="activeTabName">
        <!-- ⭐ GENERAL ⭐ -->
        <el-tab-pane label="General" name="general">
          <el-checkbox v-model="editForm.attach">Receive email</el-checkbox>
          <email-phi-warning v-if="editForm.attach" />
          <el-checkbox v-if="editForm.attach" v-model="editForm.deleteVoicemail">
            Enable storing voicemails
          </el-checkbox>
          <el-tag class="mb-10" v-if="editForm.attach">
            <i class="el-icon-info" style="margin-right: 4px" />
            This allows continued storage of voicemails after successful
            delivery
          </el-tag>
          <el-checkbox v-model="editForm.groupMailbox">Group mailbox</el-checkbox>
          <el-checkbox v-model="editForm.refillAttachments">Enable patient refill attachments</el-checkbox>
          <el-checkbox v-model="editForm.transcription" @change="onTranscriptionChange">Enable voicemail transcription</el-checkbox>
          <el-checkbox v-model="editForm.attachRecording">Enable attach voicemail recording</el-checkbox>
          <el-checkbox v-model="editForm.attachTranscription" @change="onAttachTranscriptionChange">Enable attach voicemail transcription</el-checkbox>
          <el-checkbox v-model="editForm.hipaa">Require message ID for message retrieval</el-checkbox>
          <el-checkbox v-model="editForm.disableDelete">Disable message deletion by phone</el-checkbox>

          <div class="inline-form-group">
            <el-col :span="11">
              <el-input v-model="editForm.fullName" placeholder="Full name">
                <template slot="prefix">Full name</template>
              </el-input>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="12">
              <el-input v-model="mailbox" placeholder="Mailbox" disabled>
                <template slot="prefix">Mailbox</template>
              </el-input>
            </el-col>
          </div>
          <div class="inline-form-group">
            <el-col :span="11">
              <el-input v-model="editForm.email" placeholder="Email">
                <template slot="prefix">Email</template>
              </el-input>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="12">
              <el-input v-model="editForm.password" placeholder="Pin">
                <template slot="prefix">Pin</template>
              </el-input>
            </el-col>
          </div>
          <div class="inline-form-group">
            <el-col :span="11">
              <dads-select
                v-model="editForm.branchId"
                :value="editForm.branchId"
                placeholder="Select branch"
                filterable
              >
                <template slot="prefix">Branch</template>
                <el-option
                  v-for="item in branchOptions"
                  :key="item + getRandomKey()"
                  :label="item.name"
                  :value="item.id"
                />
              </dads-select>
            </el-col>
          </div>
          <dads-select
            v-model="editForm.tz"
            placeholder="Select a Timezone"
            filterable
          >
            <template slot="prefix">Timezone</template>
            <el-option
              v-for="(timezone, index) in timezoneOptions"
              :key="index"
              :label="timezone.name"
              :value="timezone.name"
            />
          </dads-select>

          <h2 class="form-section-header">
            Call Notifications
          </h2>
          <div class="inline-form-group">
            <el-col :span="11">
              <el-input v-model="editForm.notifyNumber" placeholder="Enter number">
                <template slot="prefix">Notify number</template>
              </el-input>
            </el-col>
            <el-col :span="1" />
            <el-col :span="12">
              <dads-select
                v-model="editForm.notifyCidnum"
                :value="editForm.notifyCidnum"
                clearable
                filterable
                remote
                :remote-method="remoteMethodForNotifyCid"
                placeholder="Select caller id"
              >
                <template slot="prefix">Caller id</template>
                <el-option
                  v-for="option in cidNumOptions"
                  :key="option.id"
                  :value="option.label"
                  :label="option.label | formatNumber"
                />
              </dads-select>
            </el-col>
          </div>

          <h2 class="form-section-header">Voicemail Length</h2>
          <div class="mb-30 voicemail-length-slider">
            <vue-slider
              v-model="editForm.voicemailLength"
              :min="2"
              :max="300"
              :interval="1"
              :dot-size="20"
              :width="300"
              :lazy="true"
              :tooltip="'always'"
              :piecewise="true"
              :piecewise-label="true"
              :marks="[2,300]"
              :process-style="{ 'backgroundColor': '#222' }"
              :dot-style="{ 'borderColor': '#222', 'backgroundColor': '#fff' }"
              :range="true"
            />
            <div class="mt-10">
              <span>Selected length: {{ editForm.voicemailLength ? editForm.voicemailLength.join('-') : '2-300' }} seconds</span>
            </div>
          </div>

          <h2 class="form-section-header">Virtual receptionist settings</h2>
          <div class="mt-5">
            <el-checkbox v-model="allowIvr">Allow IVR Options</el-checkbox>
            <dads-select
              v-if="allowIvr"
              v-model="editForm.ivrId"
              :filterable="true"
              placeholder="Select IVR"
            >
              <template slot="prefix">Select IVR</template>
              <el-option
                v-for="(ivr, i) of ivrOptions"
                :key="i"
                :label="ivr.name"
                :value="ivr.id"
              />
            </dads-select>
          </div>

          <!-- ⭐ OPERATOR DESTINATION ⭐ -->
          <h2 class="form-section-header">If the operator is dialed</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
            :selectedDestinations="editForm.operatorDestinations"
            @destinationsSelected="onOperatorDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />
          <el-tag class="mb-10">
            <i class="el-icon-info" style="margin-right: 4px" />
            The operator can be dialed by pressing the 0 key
          </el-tag>
        </el-tab-pane>

        <!-- ⭐ GREETINGS ⭐ -->
        <el-tab-pane label="Greetings" name="greetings">
          <voicemail-settings
            :key="vmsettingsKey"
            :mailboxuser="editForm.mailbox"
            :mailboxContext="editForm.context"
            :mediafileOptions="mediafileOptions"
          />
        </el-tab-pane>

        <el-tab-pane label="Messages" name="messages">
          <div class="page-table">
            <!--Messages-->
            <div class="flex align-center">
              <div class="flex align-center" style="gap: 12px">
                <el-input v-model="search" placeholder="Search Message ID" clearable />
                <el-button
                  :disabled="!checkedRows.length"
                  class="mv-10"
                  type="danger"
                  plain
                  @click="bulkDelete"
                >
                  Delete
                  <i class="el-icon-delete el-icon--right icon-red"></i>
                </el-button>
              </div>

              <div class="box right">
                <el-tag>
                  <i class="el-icon-info" style="margin-right: 4px"></i>
                  Total: {{ messageCount }}
                </el-tag>
              </div>
            </div>
            <vxe-grid
              round
              border="inner"
              ref="xGrid"
              height="350px"
              auto-resize
              empty-text="No Data"
              show-overflow="tooltip"
              highlight-hover-row
              @cell-click="cellClicked"
              @cell-mouseenter="rowHover"
              @cell-mouseleave="rowLeave"
              @checkbox-all="checkAllTrigger"
              @checkbox-change="checkboxesChanged"
              :loading="tableLoading"
              :data="tableData"
              :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
              :columns="tableColumns"
            />
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-form>

    <destination-references
      :tenantId="selectedVoicemail ? selectedVoicemail.tenantId : null"
      type="VOICEMAIL"
      :id="selectedVoicemail ? selectedVoicemail.id : null"
      :clearState="clearFormFields"
    />

    <message-audio
      :modal-visible="audioModalVisible"
      :selected-message="selectedMessage"
      @closeModal="audioModalVisible = false"
    />
  </el-dialog>
</template>

<script>
import VoicemailSettings from "./voicemail-settings";
import DestinationSelector from "@/components/DestinationSelector.vue";
import DadsSelect from "@/components/select";
import EmailPhiWarning from "@/components/EmailPhiWarning.vue";
import DestinationReferences from "@/components/DestinationReferences";
import RecentAudits from "@/components/RecentAudits";
import MessageAudio from "./message-audio";
import feathersClient, {apiV2Host} from "@/feathers/feathers-client";
import moment from "moment-timezone";
import {phoneNumberFormatter} from "@/globals/globals";
import {debounce} from "lodash";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";

export default {
  name: "VoicemailEditForm",

  props: {
    editVisible: { required: true },
    clearFormFields: { required: true },
    editProcessing: { required: true },
    ivrOptions: { required: true },
    timezoneOptions: { required: true },
    draggableDestTypes: { required: true },
    selectedVoicemail: { required: true },
    cidNumOptions: { required: true, default: () => [] },
    branchOptions: { required: true }
  },

  components: {
    VoicemailSettings,
    DestinationSelector,
    DadsSelect,
    EmailPhiWarning,
    DestinationReferences,
    RecentAudits,
    MessageAudio,
    VueSlider,
  },

  data() {
    return {
      activeTabName: "general",
      allowIvr: false,
      mailbox: "",
      vmsettingsKey: "",
      tableData: [],
      tableLoading: false,
      visibleActionsId: null,
      audioModalVisible: false,
      selectedMessage: null,
      checkedRows: [],
      mediafileOptions: [],
      search: "",
      editForm: {
        tenantId: null,
        context: "",
        tz: "",
        fullName: "",
        email: "",
        password: "",
        attach: false,
        operatorDestinations: [],
        deleteVoicemail: false,
        ivrId: null,
        onlyCredentials: false,
        groupMailbox: false,
        notifyNumber: "",
        notifyCidnum: "",
        branchId:null,
        refillAttachments: false,
        transcription: false,
        attachRecording: false,
        attachTranscription: false,
        hipaa: false,
        voicemailLength: [2,120],
        disableDelete: false
      },
      tableColumns: [
        {
          field: "checkbox",
          type: "checkbox",
          width: 30,
        },
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-purple" sprite="voicemail" />
                </div>,
              ];
            },
          },
        },
        {
          field: "id",
          title: "Message ID",
          minWidth: 150,
          slots: {
            default: ({ row }) => {
              return [<span>{row.id}</span>];
            },
          },
        },
        {
          field: "origtime",
          title: "Time",
          minWidth: 150,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(+row.origTime)}</span>];
            },
          },
        },
        {
          field: "callerName",
          title: "Name",
          minWidth: 180,
          showOverflow: true,
          sortable: true,
        },
        {
          field: "callerNumber",
          title: "Number",
          minWidth: 150,
          showOverflow: true,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.numberFormatter(row.callerNumber)}</span>];
            },
          },
        },
        {
          field: "duration",
          title: "Duration",
          width: 100,
          sortable: true,
        },
        {
          field: "actions",
          title: "",
          minWidth: "100",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="mdi mdi-music-note"
                    onClick={() => this.getMessageAudio(row)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteMessage(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
    };
  },
  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    userOffsetMinutes() {
      return this.$store.state.userTimeOffset;
    },
    messageCount() {
      return this.tableData.length;
    },
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    editVisible(val) {
      if (!val) {
        this.changeVmSettingsKey();
      }
    },

    selectedTenant(){
      this.fetchMediafiles();
    },

    search: debounce(function() {
      this.searchVoicemailById();
    }, 500),

    selectedVoicemail(val) {
      if (val) {
        const {
          context,
          tz,
          mailbox,
          fullName,
          email,
          password,
          attach,
          de_destinations,
          deleteVoicemail,
          ivrId,
          messages,
          groupMailbox,
          notifyNumber,
          notifyCidnum,
          branchId,
          transcription,
          attachRecording,
          attachTranscription,
          refillAttachments,
          hipaa,
          disableDelete
        } = val;

        this.editForm.context = context;
        this.editForm.tz = tz;
        this.mailbox = mailbox;
        this.editForm.mailbox = mailbox;
        this.editForm.fullName = fullName;
        this.editForm.email = email;
        this.editForm.password = password;
        this.editForm.attach = attach === "yes";
        this.editForm.deleteVoicemail = deleteVoicemail === "no";
        this.editForm.groupMailbox = !!groupMailbox;
        this.editForm.refillAttachments = !!refillAttachments;
        this.editForm.transcription = !!transcription;
        this.editForm.attachRecording = !!attachRecording;
        this.editForm.attachTranscription = !!attachTranscription;
        this.editForm.hipaa = !!hipaa;
        this.editForm.disableDelete = !!disableDelete;
        this.editForm.notifyNumber = notifyNumber;
        this.editForm.notifyCidnum = notifyCidnum;

        if (ivrId) {
          this.editForm.ivrId = ivrId;
          this.allowIvr = true;
        } else {
          this.editForm.ivrId = null;
        }

        this.tableData = [...messages];
        this.editForm.operatorDestinations = [...de_destinations];
        this.setBranchId(branchId);
        if (Array.isArray(val.voicemailLength) && val.voicemailLength.length === 2) {
          this.$set(this.editForm, 'voicemailLength', [...val.voicemailLength]);
        } else if (
          typeof val.minLength === "number" &&
          typeof val.maxLength === "number"
        ) {
          this.$set(this.editForm, 'voicemailLength', [val.minLength, val.maxLength]);
        } else {
          this.$set(this.editForm, 'voicemailLength', [2, 300]);
        }
      }
    },
  },

  methods: {
    // Attaching a transcript requires transcription; turning transcription off clears the attach
    onAttachTranscriptionChange(val) {
      if (val) this.editForm.transcription = true;
    },
    onTranscriptionChange(val) {
      if (!val) this.editForm.attachTranscription = false;
    },
    cellClicked(data) {
      if (
        data.column.property !== "actions" &&
        data.column.property !== "checkbox"
      ) {
        this.getMessageAudio(data.row);
      }
    },
    getRandomKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },
    async setBranchId(branchId) {
      if (branchId && branchId !== 0) {
        this.editForm.branchId = branchId;
        return;
      }
      try {
        const result = await feathersClient.service("branches").find({
          query: { tenantId: this.selectedTenant.id, default: 1 }
        });
        this.editForm.branchId = result.length > 0 ? result[0].id : null;
      } catch (error) {
        console.error("Failed to fetch the branch", error);
        this.editForm.branchId = null;
      }
    },
    checkAllTrigger({ checked, records }) {
      this.checkedRows = checked ? [...records] : [];
    },

    checkboxesChanged({ checked, records }) {
      this.checkedRows = [...records];
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    timeFormatter(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment
            .unix(value)
            .subtract(offsetMinutes, "minutes")
            .format("M/DD/YY h:mm A")
        : moment
            .unix(value)
            .add(offsetMinutes, "minutes")
            .format("M/DD/YY h:mm A");
    },

    getMessageAudio(message) {
      this.audioModalVisible = true;
      this.selectedMessage = message;
    },

    deleteMessage(id) {
      this.$confirm("Delete voicemail message?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          feathersClient
            .service("voicemail-messages")
            .remove(id)
            .then((res) => {
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success",
              });
              this.tableData = this.tableData.filter((item) => item.id !== id);
            });
        })
        .catch(() => true);
    },

    async bulkDelete() {
      await this.$confirm("Delete selected messages?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(async () => {
          this.tableLoading = true;
          for (let i = 0; i < this.checkedRows.length; i++) {
            const id = this.checkedRows[i].id;
            await feathersClient
              .service("voicemail-messages")
              .remove(id)
              .then((res) => {
                this.tableData = this.tableData.filter(
                  (item) => item.id !== id
                );
              });
            await new Promise((resolve, reject) => {
              setTimeout(() => {
                resolve();
              }, 300);
            });
          }
          this.tableLoading = false;
          this.checkedRows = [];
        })
        .catch(() => true);
    },

    onOperatorDestinationsSelection(destinations) {
      this.editForm.operatorDestinations = [...destinations];
    },

    remoteMethodForNotifyCid(query) {
      this.$emit("fetchCidnumOptions", query);
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find((dst) => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Don't leave any destination field to be empty.",
          type: "error",
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        destinations.forEach((dst, idx) => (dst.order = idx + 1));

        finalDestinations = destinations.map(({ id, type, order }) => ({
          id,
          type,
          order,
        }));
      }
      return finalDestinations;
    },

    editOperation() {
      this.editForm.tenantId = this.selectedTenant.id;
      this.editForm.context = this.selectedTenant.tenantCode;
      this.editForm.tz = this.editForm.tz
        ? this.editForm.tz
        : this.selectedTenant.timezone.name;
      this.editForm.branchId = this.editForm.branchId || null;


      const editBody = { ...this.editForm };
      editBody.attach = this.editForm.attach ? "yes" : "no";
      editBody.deleteVoicemail =
        this.editForm.attach && this.editForm.deleteVoicemail ? "no" : "yes";

      editBody.groupMailbox = this.editForm.groupMailbox ? 1 : 0;
      editBody.refillAttachments = this.editForm.refillAttachments ? 1 : 0;
      editBody.transcription = this.editForm.transcription ? 1 : 0;
      editBody.attachRecording = this.editForm.attachRecording ? 1 : 0;
      editBody.attachTranscription = this.editForm.attachTranscription ? 1 : 0;
      editBody.hipaa = this.editForm.hipaa ? 1 : 0;
      editBody.disableDelete = this.editForm.disableDelete ? 1 : 0;

      //force storing voicemail if emailing disabled.
      if (editBody.attach === "no") {
        editBody.deleteVoicemail = "no";
      }

      if (this.allowIvr && this.editForm.ivrId) {
        editBody.ivrId = this.editForm.ivrId;
      } else {
        editBody.ivrId = 0;
      }
      if (
        Array.isArray(this.editForm.voicemailLength) &&
        this.editForm.voicemailLength.length === 2
      ) {
        editBody.minLength = this.editForm.voicemailLength[0];
        editBody.maxLength = this.editForm.voicemailLength[1];
      } else {
        editBody.minLength = 2;
        editBody.maxLength = 120;
      }

      const operatorDestinations = this.validateDestinations(
        editBody.operatorDestinations,
        "OPERATOR"
      );

      if (operatorDestinations) {
        editBody.operatorDestinations = operatorDestinations;
      } else {
        return;
      }

      this.$emit("editOperation", editBody);
    },
    async getDefaultBranch() {
      try {
        const [defaultBranch] = await feathersClient.service("branches").find({
          query: {
            tenantId: this.selectedTenant.id,
            default: 1
          }
        });
        this.editForm.branchId = defaultBranch.id;
      } catch (error) {
        console.log(error)
      }
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    clearFields() {
      this.activeTabName = "general";
      this.allowIvr = false;
      this.mailbox = "";
      this.editForm = {
        tenantId: null,
        context: "",
        tz: "",
        fullName: "",
        email: "",
        password: "",
        attach: false,
        operatorDestinations: [],
        deleteVoicemail: false,
        ivrId: null,
        onlyCredentials: false,
        groupMailbox: false,
        notifyCidnum: "",
        notifyNumber: "",
        branchId: null,
        refillAttachments: false,
        transcription: false,
        attachRecording: false,
        attachTranscription: false,
        hipaa: false,
        disableDelete: false,
        voicemailLength: [2, 300]
      };
      this.search = "";
      this.getDefaultBranch();
    },

    changeVmSettingsKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      this.vmsettingsKey = `${randomString()}-${randomString()}$${randomString()}`;
    },

    fetchMediafiles() {
      feathersClient
        .service("mediafiles")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
          },
        })
        .then((res) => {
          this.mediafileOptions = [...res];
        })
        .catch(() => {});
    },

    async searchVoicemailById() {
      if (this.search.length) {
        feathersClient.service('voicemail-messages').find({
          query: {
            mailboxuser: this.editForm.mailbox,
            mailboxcontext: this.editForm.context,
            id: this.search
          }
        }).then(res => {
          const voicemail = res[0];
          const match = voicemail.callerId.match(/^(.+?)\s*<([^>]+)>$/);

          if (match) {
            voicemail.callerName = match[1].includes(`"`) ? match[1].slice(1, -1) : match[1];
            voicemail.callerNumber = match[2];
          }

          this.tableData = [voicemail];
        }).catch(_err => {
          this.tableData = [];
        })
      } else {
        this.tableData = [...this.selectedVoicemail.messages];
      }
      this.total = this.tableData.length;
    }
  },

  created() {
    this.changeVmSettingsKey();
    this.fetchMediafiles();
    this.getDefaultBranch();
  },

  filters: {
    formatNumber(value) {
      return value
        ? value.replace(/(\d{3})(\d{3})(\d{4})/g, "($1) $2-$3")
        : value;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.el-form {
  max-width: 600px;
  margin: 0 auto;

  .el-checkbox {
    margin-left: 0;
  }

  h4 {
    margin: 12px 0 0 3px;
  }
}

.el-date-editor {
  width: 100%;
}

.voicemail-length-slider {
  margin-bottom: 32px;
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  align-items: left;

  .vue-slider {
    margin-top: 16px;
    margin-bottom: 8px;
    margin-left: 16px;
    width: 320px;
    padding-left: 24px;
    padding-right: 24px;
  }
}
.vue-slider-dot-tooltip-inner {
  background: #222 !important;
  border-color: #fff !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  font-weight: bold;
  font-size: 14px;
}


</style>
